type Lines = readonly [string, string, string];
type Choices = readonly [string, string, string, string];
export interface WarmupQuestion {
  id: string;
  category: 'videogames' | 'movies' | 'series' | 'anime' | 'comics' | 'music';
  correctIndex: 0 | 1 | 2 | 3;
  fr: { lines: Lines; options: Choices };
  en: { lines: Lines; options: Choices };
}
function question(id: string, category: WarmupQuestion['category'], correctIndex: WarmupQuestion['correctIndex'], fr: Lines, frOptions: Choices, en: Lines, enOptions: Choices): WarmupQuestion {
  return { id, category, correctIndex, fr: { lines: fr, options: frOptions }, en: { lines: en, options: enOptions } };
}

// Public, unscored warmups: separate from the competitive question/answer protocol.
export const warmupQuestions: readonly WarmupQuestion[] = [
  question('zelda', 'videogames', 0,
    ['Dans quelle saga', 'manie-t-on la', 'Master Sword ?'], ['The Legend of Zelda', 'Final Fantasy', 'Kingdom Hearts', 'Dragon Quest'],
    ['Which series', 'features the', 'Master Sword?'], ['The Legend of Zelda', 'Final Fantasy', 'Kingdom Hearts', 'Dragon Quest']),
  question('mario', 'videogames', 3,
    ['Comment', 's’appelle le', 'frère de Mario ?'], ['Wario', 'Yoshi', 'Toad', 'Luigi'],
    ['What is the', 'name of', 'Mario’s brother?'], ['Wario', 'Yoshi', 'Toad', 'Luigi']),
  question('star-wars', 'movies', 2,
    ['Qui est le père', 'de Luke', 'Skywalker ?'], ['Obi-Wan Kenobi', 'Han Solo', 'Dark Vador', 'Yoda'],
    ['Who is', 'Luke Skywalker’s', 'father?'], ['Obi-Wan Kenobi', 'Han Solo', 'Darth Vader', 'Yoda']),
  question('quidditch', 'movies', 1,
    ['À quel sport', 'joue Harry', 'Potter ?'], ['Football', 'Quidditch', 'Tennis', 'Échecs'],
    ['Which sport', 'does Harry', 'Potter play?'], ['Football', 'Quidditch', 'Tennis', 'Chess']),
  question('pikachu', 'videogames', 3,
    ['Quelle est', 'la couleur de', 'Pikachu ?'], ['Rouge', 'Bleu', 'Vert', 'Jaune'],
    ['What colour', 'is', 'Pikachu?'], ['Red', 'Blue', 'Green', 'Yellow']),
  question('delorean', 'movies', 2,
    ['Dans Retour vers', 'le futur, quelle', 'voiture voyage ?'], ['Ferrari', 'Mustang', 'DeLorean', 'Mini'],
    ['In Back to the', 'Future, which car', 'travels in time?'], ['Ferrari', 'Mustang', 'DeLorean', 'Mini']),
  question('friends', 'series', 1,
    ['Quel café', 'retrouve-t-on', 'dans Friends ?'], ['Luke’s Diner', 'Central Perk', 'MacLaren’s Pub', 'Double R Diner'],
    ['Which café', 'appears in', 'Friends?'], ['Luke’s Diner', 'Central Perk', 'MacLaren’s Pub', 'Double R Diner']),
  question('breaking-bad', 'series', 0,
    ['Quelle matière', 'enseigne d’abord', 'Walter White ?'], ['Chimie', 'Biologie', 'Physique', 'Mathématiques'],
    ['Which subject', 'does Walter White', 'teach at first?'], ['Chemistry', 'Biology', 'Physics', 'Mathematics']),
  question('dragon-balls', 'anime', 2,
    ['Combien faut-il', 'réunir de', 'Dragon Balls ?'], ['Cinq', 'Six', 'Sept', 'Huit'],
    ['How many', 'Dragon Balls', 'grant a wish?'], ['Five', 'Six', 'Seven', 'Eight']),
  question('luffy', 'anime', 2,
    ['Quel chapeau', 'porte', 'Luffy ?'], ['Un béret', 'Une casquette', 'Un chapeau de paille', 'Un haut-de-forme'],
    ['What kind', 'of hat does', 'Luffy wear?'], ['A beret', 'A baseball cap', 'A straw hat', 'A top hat']),
  question('batman', 'comics', 1,
    ['Quelle ville', 'protège', 'Batman ?'], ['Metropolis', 'Gotham City', 'New York', 'Asgard'],
    ['Which city', 'does', 'Batman protect?'], ['Metropolis', 'Gotham City', 'New York', 'Asgard']),
  question('superman', 'comics', 3,
    ['De quelle planète', 'est originaire', 'Superman ?'], ['Asgard', 'Coruscant', 'Vulcain', 'Krypton'],
    ['Which planet', 'is the birthplace', 'of Superman?'], ['Asgard', 'Coruscant', 'Vulcan', 'Krypton']),
  question('queen', 'music', 0,
    ['Quel groupe', 'a enregistré', 'Bohemian Rhapsody ?'], ['Queen', 'The Beatles', 'Pink Floyd', 'ABBA'],
    ['Which band', 'recorded', 'Bohemian Rhapsody?'], ['Queen', 'The Beatles', 'Pink Floyd', 'ABBA']),
  question('daft-punk', 'music', 1,
    ['Quel duo français', 'est connu pour', 'ses casques ?'], ['Air', 'Daft Punk', 'Justice', 'Phoenix'],
    ['Which French duo', 'is famous for', 'wearing helmets?'], ['Air', 'Daft Punk', 'Justice', 'Phoenix']),
];

const LAST_QUESTION_KEY = 'qpug-home-warmup-last';
let lastShownInMemory: string | null = null;
let storageWriteFailed = false;
function previousVisit(): string | null {
  if (storageWriteFailed) return lastShownInMemory;
  try { return window.localStorage.getItem(LAST_QUESTION_KEY) ?? lastShownInMemory; }
  catch { return lastShownInMemory; }
}

// Selection only reads storage; React StrictMode may run an initializer twice.
// Persist exclusively after the selected question is actually mounted.
export function nextWarmupQuestion(previousId = previousVisit()): WarmupQuestion {
  const candidates = warmupQuestions.filter(item => item.id !== previousId);
  return candidates[Math.floor(Math.random() * candidates.length)];
}
export function rememberWarmupQuestion(id: string): void {
  lastShownInMemory = id;
  try { window.localStorage.setItem(LAST_QUESTION_KEY, id); storageWriteFailed = false; }
  catch { storageWriteFailed = true; }
}
